(() => {
  if (window.__snugProductSafetyInstalled) return;
  window.__snugProductSafetyInstalled = true;

  const registry = window.customElements;
  if (!registry?.define) return;
  const define = registry.define.bind(registry);

  const cartEl = () =>
    document.querySelector('cart-notification') ||
    document.querySelector('cart-drawer');

  const sections = (cart) => {
    try {
      return cart?.getSectionsToRender?.() || [];
    } catch (e) {
      console.warn('Snug cart sections unavailable', e);
      return [];
    }
  };

  const errorText = (data, fallback) =>
    data?.description || data?.message || fallback;

  const showError = (message) => {
    if (typeof window.handleErrorMessagePopup === 'function') {
      window.handleErrorMessagePopup(message);
    } else {
      console.warn(message);
    }
  };

  const updateCart = async (cart, data, source) => {
    if (cart && data?.sections) {
      sections(cart).forEach((section) => {
        const htmlText = data.sections[section.id];
        const target = document.getElementById(section.id);
        if (!htmlText || !target) return;
        const doc = new DOMParser().parseFromString(htmlText, 'text/html');
        const fresh = doc.querySelector('#minicart-form');
        if (!fresh) return;
        const current = target.querySelector('#minicart-form');
        if (current) current.innerHTML = fresh.innerHTML;
        else target.innerHTML = fresh.innerHTML;
      });
      try { cart.cartAction?.(); } catch (e) { console.warn(e); }
    }

    let state = null;
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (res.ok) state = await res.json();
    } catch (e) {
      console.warn('Snug cart refresh failed', e);
    }

    if (state) {
      document.querySelectorAll('.cart-count').forEach((el) => {
        const value = state.item_count > 100 ? '~' : String(state.item_count);
        const hidden = el.querySelector('.visually-hidden');
        const text = [...el.childNodes].find((n) => n.nodeType === Node.TEXT_NODE);
        if (text) text.nodeValue = value;
        else if (hidden) el.insertBefore(document.createTextNode(value), hidden);
        else el.textContent = value;
      });
      try { document.querySelector('header-total-price')?.updateTotal?.(state); } catch (e) { console.warn(e); }
      try { document.querySelector('free-ship-progress-bar')?.init?.(state.items_subtotal_price); } catch (e) { console.warn(e); }
    }

    const quantity = Array.isArray(data?.items)
      ? data.items.reduce((n, item) => n + (Number(item?.quantity) || 0), 0)
      : 1;

    window.dispatchEvent(new CustomEvent('snug:cart-added', {
      detail: {
        count: state?.item_count,
        quantity: quantity || 1,
        productTitle: source,
        source
      }
    }));
  };

  const addForm = async (form, source) => {
    if (!form) throw new Error('Add-to-cart form is unavailable.');

    const cart = cartEl();
    const config = typeof window.fetchConfig === 'function'
      ? window.fetchConfig('json')
      : { method: 'POST', headers: { Accept: 'application/json' } };

    config.headers ||= {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const body = new FormData(form);
    const cartSections = sections(cart);
    if (cartSections.length) {
      body.append('sections', cartSections.map((s) => s.id).join(','));
      body.append('sections_url', window.location.pathname);
    }
    config.body = body;

    const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, config);
    const text = await res.text();
    let data = {};
    try { data = text ? JSON.parse(text) : {}; }
    catch (_) { throw new Error('The cart returned an invalid response.'); }

    if (!res.ok || data?.message || data?.status >= 400) {
      throw new Error(errorText(data, 'These items could not be added. Please try again.'));
    }

    await updateCart(cart, data, source);
    return data;
  };

  const hide = (el) => {
    el?.setAttribute('hidden', '');
    el?.setAttribute('aria-hidden', 'true');
  };

  const patchRecommendations = (Ctor) => {
    Ctor.prototype.connectedCallback = function () {
      if (this.dataset.snugSafeBound) return;
      this.dataset.snugSafeBound = '1';

      const load = async () => {
        try {
          if (!this.dataset.url) throw new Error('Recommendations URL missing.');
          const res = await fetch(this.dataset.url, { credentials: 'same-origin', cache: 'no-store' });
          if (!res.ok) throw new Error(`Recommendations request failed (${res.status}).`);
          const shell = document.createElement('div');
          shell.innerHTML = await res.text();
          const fresh = shell.querySelector('product-recommendations');
          if (!fresh?.innerHTML?.trim()) {
            this.remove();
            document.querySelector('.product-recommendations-heading')?.remove();
            return;
          }
          this.innerHTML = fresh.innerHTML;
          if (this.querySelector('.swiper-wrapper')) this.initSlide?.();
          window.BlsAnimations?.innit?.();
          window.initLazyloadItem?.();
        } catch (e) {
          console.warn('Snug recommendations skipped safely', e);
          this.remove();
          document.querySelector('.product-recommendations-heading')?.remove();
        }
      };

      if (!('IntersectionObserver' in window)) return void load();
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0]?.isIntersecting) return;
        observer.unobserve(this);
        load();
      }, { rootMargin: '0px 0px 400px 0px' });
      observer.observe(this);
    };
  };

  const patchGroup = (Ctor) => {
    Ctor.prototype.init = async function () {
      const source = this.querySelector('.productGroup[type="application/json"]');
      if (!source || this.dataset.snugSafeLoading || this.dataset.snugSafeReady) return;

      let ids;
      try { ids = JSON.parse(source.textContent || '[]'); }
      catch (e) { console.warn('Snug grouped-product data invalid', e); return hide(this); }
      if (!Array.isArray(ids) || !ids.length) return hide(this);

      this.dataset.snugSafeLoading = '1';
      try {
        const q = ids.map((id) => `id:${id}`).join(' OR ');
        const res = await fetch(`${window.routes?.search_url || '/search'}?q=${encodeURIComponent(q)}&section_id=product-grouped`, {
          credentials: 'same-origin',
          cache: 'no-store'
        });
        if (!res.ok) throw new Error(`Grouped-product request failed (${res.status}).`);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('.product-group');
        if (!fresh?.innerHTML?.trim()) throw new Error('Grouped-product markup empty.');
        this.innerHTML = fresh.innerHTML;
        this.dataset.snugSafeReady = '1';
        this.removeAttribute('hidden');
        this.removeAttribute('aria-hidden');
        this.eventProductGroupAction?.();
      } catch (e) {
        console.warn('Snug grouped products skipped safely', e);
        hide(this);
      } finally {
        delete this.dataset.snugSafeLoading;
      }
    };

    Ctor.prototype.submitProductGroup = async function (event) {
      event?.preventDefault?.();
      const button = event?.currentTarget;
      button?.classList.add('loading');
      try {
        await addForm(document.getElementById('form-product-grouped'), 'Grouped products');
      } catch (e) {
        showError(e.message || 'These items could not be added.');
      } finally {
        button?.classList.remove('loading');
      }
    };

    Ctor.prototype.submitNowProductGroup = async function (event) {
      event?.preventDefault?.();
      const button = event?.currentTarget;
      button?.classList.add('loading');
      try {
        const data = await addForm(document.getElementById('form-product-grouped'), 'Grouped products');
        if (Array.isArray(data?.items) && data.items.length) window.location.assign('/checkout');
      } catch (e) {
        showError(e.message || 'These items could not be added.');
      } finally {
        button?.classList.remove('loading');
      }
    };
  };

  const patchTogether = (Ctor) => {
    Ctor.prototype.init = async function () {
      const source =
        this.querySelector('.productBoughTogether[type="application/json"]') ||
        document.querySelector('.productBoughTogether[type="application/json"]');
      const target = document.getElementById('product-bought-together');
      if (!source || !target || this.dataset.snugSafeLoading || this.dataset.snugSafeReady) return;

      let ids;
      try { ids = JSON.parse(source.textContent || '[]'); }
      catch (e) { console.warn('Snug bought-together data invalid', e); return hide(target); }
      if (!Array.isArray(ids) || !ids.length) return hide(target);

      this.dataset.snugSafeLoading = '1';
      try {
        const q = ids.map((id) => `id:${id}`).join(' OR ');
        const res = await fetch(`${window.routes?.search_url || '/search'}?q=${encodeURIComponent(q)}&section_id=product-bough-together`, {
          credentials: 'same-origin',
          cache: 'no-store'
        });
        if (!res.ok) throw new Error(`Bought-together request failed (${res.status}).`);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('.bought-together');
        if (!fresh?.innerHTML?.trim()) throw new Error('Bought-together markup empty.');
        target.innerHTML = fresh.innerHTML;
        target.removeAttribute('hidden');
        target.removeAttribute('aria-hidden');
        this.dataset.snugSafeReady = '1';
        this.eventProductBoughTogetherAction?.();
      } catch (e) {
        console.warn('Snug bought-together products skipped safely', e);
        hide(target);
      } finally {
        delete this.dataset.snugSafeLoading;
      }
    };

    Ctor.prototype.submitBoughtTogether = async function (event) {
      event?.preventDefault?.();
      const button = event?.currentTarget;
      button?.classList.add('loading');
      try {
        await addForm(document.getElementById('bought-together-form'), 'Bought together');
      } catch (e) {
        showError(e.message || 'These items could not be added.');
      } finally {
        button?.classList.remove('loading');
      }
    };
  };

  const patch = (name, Ctor) => {
    if (!Ctor?.prototype || Ctor.prototype.__snugSafePatched) return;
    Ctor.prototype.__snugSafePatched = true;
    if (name === 'product-recommendations') patchRecommendations(Ctor);
    if (name === 'product-group') patchGroup(Ctor);
    if (name === 'product-bought-together') patchTogether(Ctor);
  };

  registry.define = function (name, Ctor, options) {
    try { patch(name, Ctor); } catch (e) { console.warn(`Snug safety patch failed for ${name}`, e); }
    return define(name, Ctor, options);
  };

  ['product-recommendations', 'product-group', 'product-bought-together'].forEach((name) => {
    const Ctor = registry.get(name);
    if (Ctor) patch(name, Ctor);
  });
})();